import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { useAuth } from '@/hooks/useAuth';
import { keys } from '@/lib/queryKeys';
import { downloadInventoryTemplate, uploadInventoryWorkbook } from './api';
import type { InventoryUploadResult } from './types';

/**
 * Pharmacy → Inventory → Upload inventory. One Excel workbook (Drug Master,
 * Hospital Formulary, Inventory) for bringing stock over from legacy software.
 * The flow is download → fill in → "Check file" (a dry run on the server:
 * nothing saved) → "Upload". Every rule lives in
 * backend/utils/pharmacyInventoryImport.js; this screen only shows its answer.
 */
const SECTIONS: Array<{ key: keyof InventoryUploadResult['summary']; label: string }> = [
  { key: 'drug_master', label: 'Drug Master' },
  { key: 'formulary', label: 'Hospital Formulary' },
  { key: 'inventory', label: 'Inventory (this branch)' },
];

export default function InventoryUploadDialog({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState<'template' | 'check' | 'upload' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InventoryUploadResult | null>(null);

  const canDrugMaster = !!user?.modules?.includes('admin:drug-master');
  const canFormulary = !!user?.modules?.includes('admin:formulary');

  const download = async () => {
    setBusy('template'); setError(null);
    try { await downloadInventoryTemplate(); } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  };

  const send = async (dryRun: boolean) => {
    if (!file) return;
    setBusy(dryRun ? 'check' : 'upload'); setError(null);
    try {
      const r = await uploadInventoryWorkbook(file, dryRun);
      setResult(r);
      if (!dryRun) {
        await queryClient.invalidateQueries({ queryKey: keys.pharmacy.inventory() });
      }
    } catch (e) {
      setError((e as Error).message);
      setResult(null);
    } finally { setBusy(null); }
  };

  const changes = result
    ? SECTIONS.reduce((n, s) => n + result.summary[s.key].created + result.summary[s.key].updated, 0)
    : 0;
  const problems = result?.results.filter((r) => r.status === 'error') ?? [];
  const notes = result?.results.filter((r) => r.status !== 'error') ?? [];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-[820px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload inventory</DialogTitle>
        </DialogHeader>

        <div className="mb-3 rounded-[var(--radius)] bg-info-soft px-3 py-2 text-xs text-info-ink">
          Branch: <b>{user?.active_branch?.name ?? '—'}</b>. The template lists this branch&apos;s stock and the upload saves
          stock to this branch only. Drug Master and Hospital Formulary are shared by every branch.
        </div>
        <ol className="mb-4 space-y-3 text-sm">
          <li className="flex flex-wrap items-center gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal text-xs font-bold text-white">1</span>
            <span className="flex-1">Download the template. It already has your current drugs, brands and this branch&apos;s stock — the first tab explains how to fill it in.</span>
            <Button variant="ghost" size="sm" disabled={busy !== null} onClick={download}>
              {busy === 'template' ? 'Preparing…' : '⬇ Download template'}
            </Button>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal text-xs font-bold text-white">2</span>
            <span>Fill in the three tabs in order — <b>Drug Master</b>, then <b>Hospital Formulary</b>, then <b>Inventory</b>. Each tab picks from the one before it using a dropdown.</span>
          </li>
          <li className="flex flex-wrap items-center gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal text-xs font-bold text-white">3</span>
            <span className="flex-1">Choose the saved file, press <b>Check file</b> to see what will change, then <b>Upload</b>.</span>
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="w-auto text-xs"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null); setError(null); }}
            />
          </li>
        </ol>

        {(!canDrugMaster || !canFormulary) && (
          <div className="mb-3 rounded-[var(--radius)] bg-warn-soft px-3 py-2 text-xs text-warn-ink">
            You can upload stock. Adding or changing{' '}
            {[!canDrugMaster && 'Drug Master', !canFormulary && 'Hospital Formulary'].filter(Boolean).join(' or ')} rows needs an
            administrator&apos;s permission — those rows will be listed as problems and skipped.
          </div>
        )}

        {error && <div className="mb-3 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}

        <div className="mb-4 flex flex-wrap gap-2">
          <Button variant="ghost" disabled={!file || busy !== null} onClick={() => send(true)}>
            {busy === 'check' ? 'Checking…' : '🔍 Check file'}
          </Button>
          <Button disabled={!file || busy !== null || !result?.dry_run || changes === 0} onClick={() => send(false)}>
            {busy === 'upload' ? 'Uploading…' : problems.length ? `⬆ Upload (skip ${problems.length} problem row${problems.length === 1 ? '' : 's'})` : '⬆ Upload'}
          </Button>
        </div>

        {result && (
          <>
            <div className={`mb-3 rounded-[var(--radius)] px-3 py-2 text-sm ${result.dry_run ? 'bg-canvas' : 'bg-success-soft text-success-ink'}`}>
              {result.dry_run
                ? changes === 0 && problems.length === 0
                  ? 'Checked — nothing to change. Everything in the file already matches.'
                  : `Checked — nothing saved yet. ${changes} row${changes === 1 ? '' : 's'} will be added or changed${problems.length ? `, ${problems.length} row${problems.length === 1 ? ' has a problem' : 's have problems'}` : ''}.`
                : `Uploaded — ${changes} row${changes === 1 ? '' : 's'} saved${problems.length ? `, ${problems.length} skipped (see below — fix them and upload the file again)` : ''}.`}
            </div>

            <Table className="mb-4">
              <TableHeader>
                <TableRow>{['Tab', 'New', 'Changed', 'Unchanged', 'Problems'].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow>
              </TableHeader>
              <TableBody>
                {SECTIONS.map((s) => {
                  const c = result.summary[s.key];
                  return (
                    <TableRow key={s.key}>
                      <TableCell className="font-semibold">{s.label}</TableCell>
                      <TableCell>{c.created}</TableCell>
                      <TableCell>{c.updated}</TableCell>
                      <TableCell className="text-ink-muted">{c.unchanged}</TableCell>
                      <TableCell className={c.failed ? 'font-bold text-danger' : ''}>{c.failed}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {problems.length > 0 && (
              <>
                <div className="mb-2 text-sm font-semibold text-danger">Problems to fix in the file</div>
                <Table className="mb-4">
                  <TableHeader>
                    <TableRow>{['Tab', 'Row', 'Item', 'What to fix'].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow>
                  </TableHeader>
                  <TableBody>
                    {problems.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell className="whitespace-nowrap">{p.sheet}</TableCell>
                        <TableCell>{p.row}</TableCell>
                        <TableCell className="text-xs text-ink-muted">{p.label ?? '—'}</TableCell>
                        <TableCell className="text-xs">{p.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}

            {notes.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer font-semibold">{result.dry_run ? 'What will change' : 'What changed'} ({notes.length})</summary>
                <Table className="mt-2">
                  <TableHeader>
                    <TableRow>{['Tab', 'Row', 'Item', '', 'Detail'].map((h, i) => <TableHead key={i}>{h}</TableHead>)}</TableRow>
                  </TableHeader>
                  <TableBody>
                    {notes.map((n, i) => (
                      <TableRow key={i}>
                        <TableCell className="whitespace-nowrap">{n.sheet}</TableCell>
                        <TableCell>{n.row}</TableCell>
                        <TableCell className="text-xs">{n.label ?? '—'}</TableCell>
                        <TableCell><Badge variant={n.status === 'created' ? 'success' : 'info'}>{n.status === 'created' ? 'New' : 'Changed'}</Badge></TableCell>
                        <TableCell className="text-xs text-ink-muted">{n.message ?? ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </details>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
